import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useGame } from '../contexts/GameContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from '../components/Toast';
import { avatarColor, initial, formatLongDate } from '../utils/format';
import './HomePage.css';

const STEPS = [
  ['Find Connections', 'Group words into sets of four that share a common theme.'],
  ['Race Against Others', 'Solve puzzles faster than your opponents to win.'],
  ['Be Careful', 'You have four lives. Each wrong guess costs one life.']
];

function DailyCard() {
  const [daily, setDaily] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get('/api/daily/today')
      .then((res) => {
        if (!cancelled) setDaily({ date: res.data.date, stats: res.data.stats });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const stats = daily?.stats;
  const players = stats?.totalAttempts || 0;

  return (
    <section className="home-daily">
      <div className="home-daily-left">
        <span className="home-daily-mark" aria-hidden="true"><i /><i /><i /><i /></span>
        <div className="home-daily-text">
          <h2>Daily Challenge</h2>
          <div className="home-daily-meta">
            <span>{formatLongDate(daily?.date)}</span>
            {stats && players > 0 && (
              <>
                <span className="cr-tnum">{players} {players === 1 ? 'player' : 'players'}</span>
                <span className="cr-tnum">{stats.solveRate}% solve rate</span>
              </>
            )}
            {stats && players === 0 && <span>No players yet</span>}
          </div>
        </div>
      </div>
      <Link to="/daily" className="cr-btn solid">Play Today's Puzzle</Link>
    </section>
  );
}

function HowToPlay() {
  return (
    <section className="home-how">
      <h2 className="cr-section-title">How to Play</h2>
      <div className="home-steps">
        {STEPS.map(([title, text], i) => (
          <div key={title} className="cr-card home-step">
            <span className={`home-step-n n${i + 1}`}>{i + 1}</span>
            <h3>{title}</h3>
            <p>{text}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function PublicGames({ games, isGuest, onJoin, onRefresh, busy }) {
  return (
    <section className="cr-card home-games">
      <div className="cr-row-head home-games-head">
        <h2 className="cr-section-title">Open Public Games</h2>
        <button type="button" className="cr-btn sm" onClick={onRefresh}>Refresh</button>
      </div>
      {games.length === 0 ? (
        <p className="home-empty">No public games right now. Create one to get started.</p>
      ) : (
        <div role="table" aria-label="Open public games">
          <div className="cr-tr cr-th" role="row">
            <span role="columnheader">Host</span>
            <span role="columnheader" className="cr-r">Players</span>
            <span role="columnheader">Type</span>
            <span role="columnheader">Status</span>
            <span role="columnheader" className="cr-r"><span className="home-sr">Action</span></span>
          </div>
          {games.map((game) => {
            const players = game.players || [];
            const host = players.find((p) => p.isHost) || players[0];
            const hostName = game.hostName || host?.username || 'Unknown';
            const count = game.playerCount ?? players.length;
            const max = game.maxPlayers || 4;
            const full = count >= max;
            const waiting = game.status === 'waiting';
            const type = game.guestOnly ? 'Guest Game' : game.isRated ? 'Rated' : 'Standard';
            const canJoin = isGuest ? game.guestOnly : true;
            return (
              <div className="cr-tr" role="row" key={game.roomId}>
                <span className="cr-player" role="cell">
                  <span className="cr-av" style={{ background: avatarColor(hostName) }}>{initial(hostName)}</span>
                  <b>{hostName}</b>
                </span>
                <span className="cr-r cr-tnum" role="cell">{count}/{max}</span>
                <span className={`cr-pill${game.isRated && !game.guestOnly ? ' rated' : ''}`} role="cell">{type}</span>
                <span className="cr-muted" role="cell">{waiting ? 'Waiting' : 'In Progress'}</span>
                <span className="cr-r" role="cell">
                  {full ? (
                    <span className="cr-faint">Full</span>
                  ) : !waiting ? null : canJoin ? (
                    <button type="button" className="cr-btn sm" disabled={busy} onClick={() => onJoin(game.roomId)}>Join</button>
                  ) : (
                    <span className="cr-faint">Log in to join</span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

const HomePage = () => {
  const navigate = useNavigate();
  const {
    createGame,
    joinGame,
    publicGames,
    fetchPublicGames,
    socket,
    connected,
    checkActiveGame,
    returnToActiveGame
  } = useGame();
  const { checkIsAuthenticated, playAsGuest, isGuest } = useAuth();
  const [roomCode, setRoomCode] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [isRated, setIsRated] = useState(false);
  const [joining, setJoining] = useState(false);
  const signedIn = checkIsAuthenticated();

  useEffect(() => {
    if (signedIn && socket && connected) {
      socket.emit('get_public_games');
    }
  }, [signedIn, socket, connected]);

  const join = async (code) => {
    const roomId = code.trim().toUpperCase();
    if (!roomId) {
      toast('Enter a room code', { error: true });
      return;
    }
    setJoining(true);
    try {
      await joinGame(roomId);
    } finally {
      setJoining(false);
    }
  };

  const handleJoinSubmit = (e) => {
    e.preventDefault();
    join(roomCode);
  };

  if (!signedIn) {
    return (
      <div className="cr-page home">
        <DailyCard />
        <section className="cr-card cr-card-pad home-login">
          <h2 className="cr-section-title">Log in to race against other players</h2>
          <div className="home-actions">
            <button type="button" className="cr-btn solid" onClick={() => navigate('/login')}>Login</button>
            <button type="button" className="cr-btn" onClick={playAsGuest}>Play as Guest</button>
          </div>
        </section>
        <HowToPlay />
      </div>
    );
  }

  return (
    <div className="cr-page home">
      {checkActiveGame() && (
        <section className="cr-card cr-card-pad cr-row-head home-active">
          <p>You have an active game in progress</p>
          <button type="button" className="cr-btn sm solid" onClick={returnToActiveGame}>Return to Game</button>
        </section>
      )}

      <DailyCard />

      <div className="home-two">
        <section className="cr-card cr-card-pad home-box">
          <h2 className="cr-section-title">Create Game</h2>
          <div className="home-checks">
            <label className="home-check">
              <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} />
              Public game
            </label>
            {!isGuest && (
              <label className="home-check">
                <input type="checkbox" checked={isRated} onChange={(e) => setIsRated(e.target.checked)} />
                Rated game
              </label>
            )}
          </div>
          <button type="button" className="cr-btn solid" onClick={() => createGame({ isPublic, isRated: !isGuest && isRated })}>
            Create Game
          </button>
        </section>

        <section className="cr-card cr-card-pad home-box">
          <h2 className="cr-section-title">Join Game</h2>
          <form className="home-join" onSubmit={handleJoinSubmit} autoComplete="off">
            <input
              className="cr-input home-code"
              value={roomCode}
              onChange={(e) => setRoomCode(e.target.value.toUpperCase())}
              placeholder="Enter room code"
              aria-label="Room code"
              maxLength={8}
              autoCapitalize="characters"
              spellCheck={false}
            />
            <button type="submit" className="cr-btn" disabled={joining}>Join</button>
          </form>
          <p className="cr-label">Ask the host for their 4 letter code.</p>
        </section>
      </div>

      <PublicGames
        games={publicGames || []}
        isGuest={isGuest}
        busy={joining}
        onJoin={join}
        onRefresh={fetchPublicGames}
      />

      <HowToPlay />
    </div>
  );
};

export default HomePage;
